import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Observable } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { PromoPrice, PromoPriceFilters } from '@models/promo-price-model';
import { PromoPriceService } from './promo-price.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isNilty } from '@core/utils.service';
import { MatDialog } from '@angular/material/dialog';
import { GenericConfirmationModalComponent } from '@shared/generic-confirmation-modal/generic-confirmation-modal.component';
import { Permissions } from '@models/security/permission-model';
import { AuthorizationService } from '@security/authorization.service';

@Component({
  selector: 'app-promo-price',
  templateUrl: './promo-price.component.html',
  styleUrls: ['./promo-price.component.css'],
})
export class PromoPriceComponent implements OnInit {
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild('promoPriceFile', { static: false }) promoPriceFile: ElementRef;

  currentFileUpload: File;

  mainToggle: boolean;
  selectedPromoPrices: string[] = [];

  hasPromoPriceManagement: Observable<boolean>;

  resultsNumber: Observable<number>;
  dataSource = new MatTableDataSource<PromoPrice>([]);
  displayedColumns = [
    'checkbox',
    'storeCode',
    'supplierCode',
    'stockLocationCode',
    'barcode',
    'price',
    'startDate',
    'endDate',
    'fileUrl',
    'loadingDate',
    'email',
  ];

  constructor(
    private promoPriceService: PromoPriceService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private authorizationService: AuthorizationService
  ) {}

  ngOnInit(): void {
    this.hasPromoPriceManagement = this.authorizationService.hasMarkusPermission(Permissions.PromoPriceManagement);

    this.resultsNumber = this.promoPriceService.resultsNumber;

    if (isNilty(this.promoPriceService.promoPriceFilters)) {
      this.promoPriceService.promoPriceFilters = new PromoPriceFilters();
    }
    this.getPromoPrices();
  }

  getPromoPrices() {
    this.promoPriceService.getPromoPricesByFilters().subscribe((resp: PromoPrice[]) => {
      this.dataSource.data = resp;
    });
  }

  loadPage() {
    this.promoPriceService.promoPriceFilters.pageNumber = this.paginator.pageIndex;
    this.promoPriceService.promoPriceFilters.pageSize = this.paginator.pageSize;
    this.getPromoPrices();
  }

  onSort() {
    this.promoPriceService.promoPriceFilters.sortBy = this.sort.active;
    this.promoPriceService.promoPriceFilters.sortDirection = this.sort.direction;
    this.getPromoPrices();
  }

  applyFilter(filters: PromoPriceFilters) {
    this.promoPriceService.promoPriceFilters = filters;
    this.paginator.pageIndex = 0;
    this.getPromoPrices();
  }

  uploadFile() {
    const message = `------------------- UPLOADING PROMO PRICE FILE -------------------\n
    The file must be a CSV file with the following columns in this order:\n
    storeCode, barcode, price, currency, startDate(dd/MM/yyyy), endDate, supplierCode, stockLocationCode\n`;

    this.dialog
      .open(GenericConfirmationModalComponent, {
        data: message,
      })
      .afterClosed()
      .subscribe((resp: boolean) => {
        if (resp) {
          this.promoPriceFile.nativeElement.click();
        }
      });
  }

  fileChanged(event) {
    this.currentFileUpload = event.target.files[0];
    if (this.currentFileUpload.type !== 'csv') {
      this.snackbar.open('Only CSV files are allowed', 'Close', { duration: 2000 });
    }
    this.promoPriceService.uploadPromoPriceFile(this.currentFileUpload).subscribe(() => {
      // Do nothing
    });
  }

  toggleAll(check: boolean) {
    this.promoPriceService.mainToggle = check;
    if (check) {
      this.selectedPromoPrices = this.dataSource.data.map((promoPrice) => promoPrice.id);
    } else {
      this.selectedPromoPrices = [];
    }
  }

  toggleSelection(id: string) {
    const index = this.selectedPromoPrices.indexOf(id);
    if (index === -1) {
      this.selectedPromoPrices.push(id);
    } else {
      this.selectedPromoPrices.splice(index, 1);
    }
    this.mainToggle = this.selectedPromoPrices.length === this.dataSource.data.length;
    this.promoPriceService.mainToggle = this.mainToggle;
  }

  onDelete() {
    const message = `DELETING SELECTED PROMO PRICES\n
    This action cannot be undone.\n`;

    this.dialog
      .open(GenericConfirmationModalComponent, {
        data: message,
      })
      .afterClosed()
      .subscribe((resp: boolean) => {
        if (resp) {
          this.promoPriceService.deletePromoPrices(this.selectedPromoPrices).subscribe(() => {
            this.getPromoPrices();
            this.selectedPromoPrices = [];
            this.promoPriceService.mainToggle = this.mainToggle = false;
          });
        }
      });
  }
}
